import React, { useMemo } from 'react'
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { formatPhoneNumberForDisplay } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

export interface SystemInterruptionModalProps {
  isModalOpen: boolean
  onClose: () => void
  lastUpdatedTime?: number | null
  appointments: Appointment[]
}

const Container = styled.View(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const HeaderContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const ContentContainer = styled.View(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const ScheduleBox = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSection,
  borderRadius: 8,
  gap: theme.spacing['4px'],
  padding: theme.spacing['16px'],
}))

const AppointmentRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
})

const AppointmentInfo = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const ConfirmationButton = styled(PrimaryButton)({
  alignSelf: 'flex-end',
})

export const SystemInterruptionModal: React.FC<SystemInterruptionModalProps> = ({
  isModalOpen,
  onClose,
  lastUpdatedTime,
  appointments,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const today = new Date()
  const todayKey = today.toDateString()

  const sortedAppointments = useMemo(() => {
    return [...appointments]
      .filter((appt) => getDateFromAppointment(appt).toDateString() === todayKey)
      .sort((a, b) => getDateFromAppointment(a).getTime() - getDateFromAppointment(b).getTime())
  }, [appointments, todayKey])

  const modalContents = (
    <Container testID={tID('SystemInterruptionModal-container')}>
      <HeaderContainer>
        <Title
          size={TitleSize.MEDIUM}
          text={formatMessage({
            defaultMessage: 'Connection issue',
            description: 'Title for the system interruption modal',
          })}
          testID={tID('SystemInterruptionModal-title')}
        />
        <PressableOpacity
          testID={tID('SystemInterruptionModal-close')}
          onPress={onClose}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close',
            description: 'Accessibility label for close button',
          })}
        >
          <XIcon size={24} fillColor={colors.iconDefault} />
        </PressableOpacity>
      </HeaderContainer>
      <ContentContainer testID={tID('SystemInterruptionModal-content')}>
        <BodyText
          testID={tID('SystemInterruptionModal-description')}
          text={
            <FormattedMessage
              defaultMessage='Some features are temporarily unavailable due to a connection issue. Please use the contact information below to contact your clients. The schedule below was last refreshed on {date} at {time} and may not reflect more recent changes such as new appointment bookings, reschedules or cancellations.'
              description='System interruption modal description with last refresh time'
              values={{
                date: lastUpdatedTime ? (
                  <FormattedDate
                    value={lastUpdatedTime}
                    weekday='short'
                    month='long'
                    day='numeric'
                    timeZone={timeZone}
                  />
                ) : (
                  ''
                ),
                time: lastUpdatedTime ? (
                  <FormattedTime
                    value={lastUpdatedTime}
                    hour='numeric'
                    minute='numeric'
                    timeZoneName='short'
                    timeZone={timeZone}
                  />
                ) : (
                  ''
                ),
              }}
            />
          }
          wrap
        />
        <ScheduleBox testID={tID('SystemInterruptionModal-schedule')}>
          <BodyText
            bold
            testID={tID('SystemInterruptionModal-scheduleHeader')}
            size={BodyTextSize.SMALL}
            text={
              <FormattedMessage
                defaultMessage='Clients for today - {date} ({tz})'
                description='Header for today schedule section in system interruption modal'
                values={{
                  date: <FormattedDate value={today} weekday='short' month='long' day='numeric' timeZone={timeZone} />,
                  tz:
                    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone })
                      .formatToParts(today)
                      .find((p) => p.type === 'timeZoneName')?.value ?? timeZone,
                }}
              />
            }
          />
          {sortedAppointments.length > 0 ? (
            <View testID={tID('SystemInterruptionModal-appointmentsInfo')}>
              {sortedAppointments.map((appt) => {
                const apptDate = getDateFromAppointment(appt)
                const clientName = appt.userInfo ? `${appt.userInfo.firstName} ${appt.userInfo.lastName}` : ''
                const phone = formatPhoneNumberForDisplay(appt.userInfo?.phone || appt.phone)
                return (
                  <AppointmentRow
                    key={appt.appointmentId}
                    testID={tID(`SystemInterruptionModal-appointment-${appt.appointmentId}`)}
                  >
                    <AppointmentInfo>
                      <BodyText
                        testID={tID('SystemInterruptionModal-appointmentTime')}
                        text={<FormattedTime value={apptDate} hour='numeric' minute='numeric' timeZone={timeZone} />}
                        color={colors.textPrimary}
                        size={BodyTextSize.SMALL}
                      />
                      <BodyText
                        testID={tID('SystemInterruptionModal-appointmentClientName')}
                        text={` · ${clientName}`}
                        color={colors.textSecondary}
                        size={BodyTextSize.SMALL}
                      />
                    </AppointmentInfo>
                    <BodyText
                      testID={tID('SystemInterruptionModal-appointmentPhone')}
                      text={phone}
                      color={colors.textSecondary}
                      size={BodyTextSize.SMALL}
                    />
                  </AppointmentRow>
                )
              })}
            </View>
          ) : (
            <BodyText
              testID={tID('SystemInterruptionModal-noAppointmentsText')}
              text={'No appointments scheduled for today'}
              color={colors.textSecondary}
              size={BodyTextSize.SMALL}
            />
          )}
        </ScheduleBox>
      </ContentContainer>
      <ConfirmationButton testID={tID('SystemInterruptionModal-confirmationButton')} text='Dismiss' onPress={onClose} />
    </Container>
  )

  return (
    <Modal
      onCloseEnd={onClose}
      onRequestClose={onClose}
      disableBottomSheet
      visible={isModalOpen}
      modalContents={modalContents}
      width='550px'
      closeOnScrim
      showCloseIcon={false}
      accessibilityLabel={formatMessage({
        defaultMessage: 'System interruption modal',
        description: 'Accessibility label for the system interruption modal',
      })}
    />
  )
}
